/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Star } from './Star';
import styles from './Rating.module.scss';
import { roundValueToPrecision } from './utils';
import { useLabels } from '@/hooks/useLabels';

type Props = {
  /**
   * The rating value.
   */
  value: number;
  max?: number;
  ariaLabel?: string;
  showStarFractions?: boolean;
  isOosOrDoesNotFit?: boolean;
  isCertonaPartCard?: boolean;
  classes?: { root?: string; icon?: string };
  filledColor?: string;
  emptyColor?: string;
};

const precision = 0.1;
const maxValue = 5;

const labelMap = {
  outOfFiveStars: 'label_Carosuel_OutOfFiveStars',
};

function Rating({
  value: valueProp,
  max,
  ariaLabel,
  showStarFractions = false,
  isOosOrDoesNotFit,
  isCertonaPartCard,
  classes,
  filledColor = '#f26100',
  emptyColor = 'rgba(0, 0, 0, 0.26)',
}: Props) {
  const value = roundValueToPrecision(valueProp, precision);
  const labels = useLabels(labelMap);

  return (
    <span
      className={styles.root}
      role={'img'}
      aria-label={ariaLabel || `${value} ${labels.outOfFiveStars}`}
    >
      {Array.from(new Array(max ?? maxValue)).map((_, index) => {
        const itemValue = index + 1;
        const itemDecimalValue = roundValueToPrecision(itemValue, precision);
        const fraction = showStarFractions
          ? 1 - Math.max(0, itemDecimalValue - value)
          : itemDecimalValue <= value
          ? 1
          : 0;
        return (
          <span key={itemValue} className={styles.icon} aria-hidden="true">
            <Star
              percent={roundValueToPrecision(fraction * 100, 0.01)}
              isOosOrDoesNotFit={isOosOrDoesNotFit}
              isCertonaPartCard={isCertonaPartCard}
              classes={classes}
              filledColor={filledColor}
              emptyColor={emptyColor}
            />
          </span>
        );
      })}
    </span>
  );
}

export { Rating };
